
import React, { createContext, useContext, useEffect, useState } from "react";

type Language = "en-US" | "pt-BR";

type LanguageProviderProps = {
  children: React.ReactNode;
  defaultLanguage?: Language;
  storageKey?: string;
};

type LanguageProviderState = {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
};

const initialState: LanguageProviderState = {
  language: "en-US",
  setLanguage: () => null,
  t: (key: string) => key,
};

const LanguageProviderContext = createContext<LanguageProviderState>(initialState);

// Expanded translations dictionary to cover the entire website
const translations: Record<Language, Record<string, string>> = {
  "en-US": {
    // Navigation
    "nav.home": "Home",
    "nav.products": "Products",
    "nav.pricing": "Pricing",
    "nav.contactUs": "Contact Us",
    
    // Products
    "products.discoverMore": "Discover More",
    
    // Theme & Language
    "theme.light": "Light Mode",
    "theme.dark": "Dark Mode",
    "lang.en": "English",
    "lang.pt": "Português",
    
    // Hero Section
    "hero.tagline": "Secure Identities Beyond Passwords",
    "hero.title": "Embrace a Passwordless Digital Experience",
    "hero.description": "Transform how you interact online with our passwordless security solutions, designed for effortless access without compromising safety.",
    "hero.explorePricing": "Explore Pricing",
    "hero.exploreProducts": "Explore Products",
    
    // Features Section
    "features.title": "Comprehensive Platform Features",
    "features.description": "Everything you need to efficiently manage organizations, billing structures, and access tiers.",
    "features.multiOrg": "Multi-Organization Management",
    "features.multiOrgDesc": "Create and manage multiple organizations under a single client account for streamlined operations.",
    "features.billing": "Flexible Billing Structure",
    "features.billingDesc": "Choose between subscription-based or usage-based billing models tailored to your needs.",
    "features.access": "Tiered Access Controls",
    "features.accessDesc": "Define different access tiers from Basic to Enterprise with granular permission settings.",
    "features.roles": "User Role Management",
    "features.rolesDesc": "Assign users to specific organizations with customizable roles and permissions.",
    "features.identityDirectory": "Universal Identity Directory",
    "features.identityDirectoryDesc": "No more scattered user data. Store profiles, manage credentials, and apply access policies seamlessly in one secure location.",
    "features.analytics": "Usage Analytics",
    "features.analyticsDesc": "Detailed metrics and reports on consumption patterns across all your organizations.",
    "features.integration": "Integration Capabilities",
    "features.integrationDesc": "Connect with external systems through comprehensive API endpoints.",
    "features.multiTenant": "Multi-Tenant Architecture",
    "features.multiTenantDesc": "Efficiently scale your operations with our optimized multi-tenant infrastructure.",
    
    // Products Page
    "products.solutions": "Our Solutions",
    "products.title": "Identity Management Solutions",
    "products.description": "Our identity management solutions offer secure, seamless, and scalable authentication. Designed to enhance security and improve user experience, our products ensure compliance with modern authentication standards.",
    "products.workforce": "Keystle Workforce",
    "products.workforceDesc": "The cornerstone of passwordless authentication, offering a secure and seamless way to manage end-user access to applications, APIs, and services. Designed for scalability and versatility to enhance security while delivering superior user experiences.",
    "products.b2b": "Keystle B2B",
    "products.b2bDesc": "A robust identity management solution designed to help businesses handle millions of customer identities securely and efficiently. Empowers organizations to deliver personalized user experiences while maintaining compliance with stringent data privacy regulations.",
    "products.ciam": "Keystle CIAM",
    "products.ciamDesc": "Designed to provide businesses with a frictionless, secure, and scalable way to manage customer identities across multiple platforms. Enables passwordless authentication, social login, adaptive security, and real-time telemetry.",
    "products.keyFeatures": "Key Features",
    
    // Security Section
    "security.trustSecurity": "Trust & Security",
    "security.title": "Security & Compliance",
    "security.description": "At Keystle, security and compliance are non-negotiable. Our comprehensive approach to protecting your data and ensuring regulatory compliance provides peace of mind for your organization and your customers.",
    "security.architecture": "Robust Security Architecture",
    "security.architectureDesc": "Our platform is built with security-first approach, implementing industry-leading encryption, secure communications, and strict access controls.",
    "security.encryption": "Encryption Standards",
    "security.encryptionDesc": "All sensitive data is encrypted at rest and in transit using AES-256 and TLS 1.3, ensuring your information remains protected across all touchpoints.",
    "security.compliance": "Compliance Requirements",
    "security.complianceDesc": "Keystle maintains compliance requirements with global regulations including GDPR, CCPA, ISO 27001, SOC 2, and other industry-specific frameworks.",
    "security.dataProtection": "Data Protection",
    "security.dataProtectionDesc": "We implement strict data segregation, regular security audits, and comprehensive monitoring to protect your sensitive information.",
    
    // Pricing Page
    "pricing.title": "Flexible Pricing Structure",
    "pricing.description": "Choose between subscription-based or usage-based pricing models. Pay monthly or annually with options to centralize or individualize billing across your organizations.",
    "pricing.plans": "Flexible Pricing Plans",
    "pricing.plansDesc": "Choose the perfect plan for your organization's needs with our transparent pricing structure.",
    "pricing.monthly": "Monthly",
    "pricing.annually": "Annually",
    "pricing.save": "Save",
    "pricing.withAnnual": "with annual billing",
    "pricing.min1500": "$1500 minimum monthly charge applies",
    "pricing.min5000": "Minimum 5000 users monthly",
    "pricing.perMonth": "/month",
    "pricing.perYear": "/year",
    "pricing.startWith": "Start with",
    "pricing.contactSales": "Contact Sales",
    
    // Tiers Page
    "tiers.title": "Tiered Access Structure",
    "tiers.description": "Define access levels for different types of clients and organizations. Each tier provides a different set of features and capabilities to match your specific needs.",
    "tiers.viewPricing": "View Pricing",
    "tiers.comparison": "Access Tier Comparison",
    "tiers.comparisonDesc": "Compare our access tiers to find the perfect fit for your organization's needs",
    "tiers.basic": "Basic",
    "tiers.basicDesc": "Essential features for small teams",
    "tiers.standard": "Standard",
    "tiers.standardDesc": "Advanced features for growing teams",
    "tiers.enterprise": "Enterprise",
    "tiers.enterpriseDesc": "Complete solution for large organizations",
    "tiers.select": "Select",
    "tiers.tier": "Tier",
    
    // Error and Coming Soon Pages
    "error.404": "Error 404",
    "error.title": "Page Not Found",
    "error.description": "Sorry, we couldn't find the page you're looking for. It might have been moved or doesn't exist.",
    "error.returnHome": "Return to Home",
    "comingSoon": "Coming Soon",
    "comingSoon.title": "Sorry for the Inconvenience",
    "comingSoon.description": "We are building an amazing product and this page is coming soon. Stay tuned for exciting updates as we continue to develop features that will transform your digital identity experience.",
    
    // Footer
    "footer.product": "Product",
    "footer.resources": "Resources",
    "footer.company": "Company",
    "footer.legal": "Legal",
    "footer.features": "Features",
    "footer.docs": "Documentation",
    "footer.api": "API Reference",
    "footer.integrations": "Integrations",
    "footer.support": "Support",
    "footer.about": "About",
    "footer.blog": "Blog",
    "footer.careers": "Careers",
    "footer.contact": "Contact",
    "footer.privacy": "Privacy",
    "footer.terms": "Terms",
    "footer.security": "Security",
    "footer.copyright": "All rights reserved.",
    "footer.description": "Empowering organizations with flexible billing and access management solutions.",
    
    // About Page
    "about.aboutKeystle": "About Keystle",
    "about.heroTitle": "Empowering a Passwordless Tomorrow",
    "about.heroSubtitle": "Secure Identity Beyond Password",
    "about.introTitle": "Welcome to Keystle",
    "about.introPara1": "In the digital age, identity is more than just a login—it is the key to secure, seamless, and frictionless experiences. However, traditional authentication methods are flawed: passwords are inconvenient, vulnerable, and compromise both security and user experience.",
    "about.introPara2": "We are motivated to transform this reality and redefine authentication. We eliminated passwords and made access more secure, intuitive, and intelligent. Our technology combines artificial intelligence, advanced telemetry, and passwordless authentication, ensuring maximum protection without compromising usability.",
    "about.introHighlight": "More security. Less friction. Limitless identity.",
    "about.feature1Title": "Passwordless Security",
    "about.feature1Desc": "Experience enhanced security with our passwordless solutions, eliminating authentication barriers while safeguarding user identities.",
    "about.feature2Title": "Seamless Integration",
    "about.feature2Desc": "Our solutions are designed for easy integration across platforms, streamlining user experiences and maximizing operational efficiency.",
    "about.feature3Title": "AI-Driven Technology",
    "about.feature3Desc": "Leverage advanced AI-driven tools that continuously adapt, ensuring robust security and user-friendly access to digital resources.",
    "about.historyTitle": "OUR HISTORY",
    "about.historyHeading": "What We Think about the Current Identity World",
    "about.historySubheading": "Why We Founded Keystle?",
    "about.historyPara1": "Our vision of the modern identity landscape drove us to seek something better. We recognized a fundamental flaw in how digital identities are managed, security is costly yet ineffective, while users endure outdated and frustrating authentication processes, whether as employees or customers.",
    "about.historyPara2": "What's the problem? A broken system that treats identity as a hurdle rather than an enabler.",
    "about.historyHighlight": "We refuse to accept this.",
    "about.visionHeading": "What if identity could be secure without being a hassle?",
    "about.visionPara1": "That's why we built an IDaaS (Identity-as-a-Service) platform designed with the user experience at its core, ensuring seamless, secure access for both employees and customers.",
    "about.visionPara2": "Our passwordless solutions don't just enhance security, they redefine it. Using AI-driven authentication and advanced telemetry, we provide frictionless protection where identity is invisible when everything is right and impenetrable when something is wrong.",
    "about.visionPara3": "We challenge the outdated model because we believe identity should work for people, not against them. A world without passwords isn't just possible, it's necessary.",
    "about.visionPara4": "Join us in building a future where access is effortless, security is seamless, and trust is built in.",
    "about.quote": "Life without passwords: Your gateway to seamless security.",
    "about.quoteContext": "We create a revolution way for businesses to approach security. Our innovative IAM solutions have helped numerous organizations enhance security and user satisfaction, reducing login friction and increasing operational efficiency. Join us on a journey towards a passwordless future.",
    "about.quoteAttribution": "— Danilo, Deli, and Gilmar, Keystle Founders",
    "about.purposeTitle": "OUR PURPOSE",
    "about.purposeSubtitle": "Defining Our Mission",
    "about.purposeText": "Empower businesses and individuals by delivering cutting-edge, passwordless Identity and Access Management (IAM) solutions that eliminate the need for passwords while ensuring security, ease of use, and scalability. With relentless innovation in AI and telemetry, we strive to safeguard digital identities, simplify access, and redefine user experiences, enabling a secure and frictionless future in an increasingly digital landscape.",
    "about.principlesTitle": "CORE PRINCIPLES",
    "about.principlesSubtitle": "Our Guiding Values",
    "about.principlesText": "Security, innovation, and seamless experiences define Keystle. We are committed to trust, accessibility, and cutting-edge technology, ensuring that identity management is both secure and effortless. By prioritizing Identity as a Service, user-first design, and continuous learning, we empower businesses with passwordless, scalable, and compliant solutions that redefine how people interact with digital identity.",
    "about.ctaTitle": "Ready to Experience Passwordless Security?",
    
    // Workforce Page
    "workforce.title": "WORKFORCE",
    "workforce.hero.title": "No Passwords, Just Security",
    "workforce.hero.description": "Stop resetting passwords, start securing access. No passwords. No friction. No breaches.",
    
    "workforce.intro.title": "Increase Security, Deliver Amazing Experiences",
    "workforce.intro.description": "We deliver unmatched security and seamless user experiences at scale. Designed for versatility, it empowers organizations with modern authentication, multi-tenancy, and dynamic access control adapting effortlessly to enterprise needs.",
    "workforce.intro.imageAlt": "Business identity management illustration",
    "workforce.getStarted": "Get Started",
    
    "workforce.features.title": "Key Features",
    "workforce.features.description": "Comprehensive authentication and access management solutions designed for enterprise requirements.",
    
    "workforce.features.authentication.title": "Authentication",
    "workforce.features.authentication.item1": "OTP and Email MFA",
    "workforce.features.authentication.item2": "OAuth 2.0, OIDC, SAML support",
    "workforce.features.authentication.item3": "Passwordless First design",
    "workforce.features.authentication.item4": "Cloud IAM integration (Google, AWS)",
    
    "workforce.features.sso.title": "Single Sign-On (SSO)",
    "workforce.features.sso.item1": "Centralized login for multiple applications",
    "workforce.features.sso.item2": "Reduced login fatigue",
    "workforce.features.sso.item3": "Streamlined enterprise integration",
    "workforce.features.sso.item4": "Cloud services compatibility",
    
    "workforce.features.scim.title": "Cross-domain Identity (SCIM)",
    "workforce.features.scim.item1": "Automated user provisioning",
    "workforce.features.scim.item2": "Reduced administrative overhead",
    "workforce.features.scim.item3": "Identity attribute synchronization",
    "workforce.features.scim.item4": "Provider interoperability",
    
    "workforce.features.authorization.title": "Dynamic Authorization",
    "workforce.features.authorization.item1": "Role-Based Access Control (RBAC)",
    "workforce.features.authorization.item2": "Attribute-Based Access Control (ABAC)",
    "workforce.features.authorization.item3": "Policy-Based Access Control (PBAC)",
    "workforce.features.authorization.item4": "Adaptive, context-aware security",
    
    "workforce.addons.title": "Add-ons",
    "workforce.addons.description": "Enhanced security capabilities to protect your organization from emerging threats.",
    
    "workforce.addons.ai.title": "AI-Powered Security",
    "workforce.addons.ai.description": "Real-time detection and prevention powered by AI to identify and block suspicious activities, with comprehensive breach detection for all identities.",
    
    "workforce.addons.mfa.title": "MFA Enterprise",
    "workforce.addons.mfa.description": "Multi-Factor Authentication with Whatsapp, Call, SMS factors for an additional layer of security, ensuring compliance with regulatory standards.",
    
    "workforce.addons.threat.title": "Threat Mitigation",
    "workforce.addons.threat.description": "Risk-based authentication adjusts security requirements based on contextual factors, while AI-driven fraud detection and behavioral analytics minimize potential threats.",
    
    "workforce.useCases.title": "Use Cases",
    "workforce.useCases.description": "Real-world applications of KEYSTLE Workforce that deliver tangible benefits.",
    
    "workforce.useCases.global.title": "Global Workforce Authentication",
    "workforce.useCases.global.item1": "Secure, passwordless access for distributed workforce",
    "workforce.useCases.global.item2": "Granular conditional authorization policies",
    "workforce.useCases.global.item3": "Context-aware security for remote employees",
    "workforce.useCases.global.item4": "Protection for hybrid work environments",
    
    "workforce.useCases.corporate.title": "Corporate SSO Deployment",
    "workforce.useCases.corporate.item1": "Simplified login across enterprise applications",
    "workforce.useCases.corporate.item2": "High security and compliance standards",
    "workforce.useCases.corporate.item3": "Reduced IT overhead for password resets",
    "workforce.useCases.corporate.item4": "Streamlined account recovery processes",
    
    "workforce.capabilities.title": "Capabilities",
    "workforce.capabilities.description": "Enterprise-grade performance and compliance features built for modern organizations.",
    
    "workforce.capabilities.performance.title": "Performance and Scalability",
    "workforce.capabilities.performance.latency.title": "Low-Latency Login",
    "workforce.capabilities.performance.latency.description": "Process millions of simultaneous authentication requests without performance degradation.",
    "workforce.capabilities.performance.availability.title": "High Availability",
    "workforce.capabilities.performance.availability.description": "Uninterrupted service with robust failover mechanisms and load balancing.",
    "workforce.capabilities.performance.residency.title": "Data Residency",
    "workforce.capabilities.performance.residency.description": "Your data at the region you choose.",
    "workforce.capabilities.performance.database.title": "Universal Database",
    "workforce.capabilities.performance.database.description": "Single source enabled by cloud directory and enterprise integrations.",
    
    "workforce.capabilities.compliance.title": "Compliance",
    "workforce.capabilities.compliance.gdpr.title": "GDPR Compliance",
    "workforce.capabilities.compliance.gdpr.description": "Data protection measures for EU regulations including user consent management.",
    "workforce.capabilities.compliance.hipaa.title": "HIPAA Standards",
    "workforce.capabilities.compliance.hipaa.description": "Secure handling of protected health information with strict access controls.",
    "workforce.capabilities.compliance.lgpd.title": "LGPD Framework",
    "workforce.capabilities.compliance.lgpd.description": "Compliance with Brazil's data protection law for data subject rights.",
    "workforce.capabilities.compliance.custom.title": "Customizable Compliance",
    "workforce.capabilities.compliance.custom.description": "Adaptable framework for industry-specific regulations with audit capabilities.",
    
    "workforce.cta.title": "Ready to secure your workforce?",
    "workforce.cta.description": "Discover how Workforce can transform your organization's security posture while enhancing user experience.",
    "workforce.viewPricing": "View Pricing",
    
    // Business Page
    "business.title": "BUSINESS",
    "business.hero.title": "Enabling Business through Identity Management",
    "business.hero.description": "Manage millions of identities with unmatched security and efficiency.",
    
    "business.intro.title": "Advanced B2B Identity Management",
    "business.intro.description": "Business Identity Manager is the ultimate solution for businesses seeking smart access control, advanced personalization, and strict privacy compliance. With seamless scalability and cutting-edge integrations, it delivers a frictionless experience for users while ensuring robust data protection.",
    "business.intro.imageAlt": "Business identity management illustration",
    "business.getStarted": "Get Started",
    
    "business.features.title": "Key Features",
    "business.features.description": "Comprehensive identity management solutions designed for modern business requirements.",
    
    "business.features.dashboard.title": "Unified Dashboard",
    "business.features.dashboard.description": "A single pane of glass for user activity, compliance tracking, and policy enforcement for distinct applications and multiple tenants.",
    
    "business.features.multiTenant.title": "Multi-Tenant Identity Management",
    "business.features.multiTenant.description": "Secure and scalable authentication for multiple clients, partners, and internal teams with isolated environments.",
    
    "business.features.passwordless.title": "Passwordless Authentication",
    "business.features.passwordless.description": "Eliminate passwords with biometrics, passkeys, and magic links for a frictionless user experience and enhanced security.",
    
    "business.features.sso.title": "Single Sign-On (SSO)",
    "business.features.sso.description": "Seamless access across multiple applications with one authentication, reducing friction and improving security.",
    
    "business.features.mfa.title": "MFA with OTP and Email",
    "business.features.mfa.description": "Secure login experiences using one-time passwords via App authenticator and email verification, providing an additional layer of security beyond passwords.",
    
    "business.features.api.title": "API-First Approach",
    "business.features.api.description": "Seamless integration with OAuth, SAML, OpenID Connect, and SCIM for fast deployment and comprehensive interoperability.",
    
    "business.addons.title": "Security & Intelligence Add-ons",
    "business.addons.description": "Enhanced security capabilities to protect your organization and customers.",
    
    "business.addons.analytics.title": "Behavioral Analytics",
    "business.addons.analytics.description": "Built-in tools analyze user behavior and access patterns to detect anomalies, prevent threats, and improve engagement.",
    
    "business.addons.fraud.title": "Fraud Prevention",
    "business.addons.fraud.description": "AI-powered device fingerprinting and risk scoring to prevent account takeovers and unauthorized access attempts.",
    
    "business.addons.adaptive.title": "Adaptive Authentication",
    "business.addons.adaptive.description": "Context-aware authentication adjusts security levels dynamically based on user activity, location, and risk factors.",
    
    "business.performance.title": "Enterprise-Grade Performance",
    "business.performance.description": "Built to handle high-volume authentication needs without compromising on speed or reliability.",
    
    "business.performance.architecture.title": "High-Performance Architecture",
    "business.performance.architecture.item1": "Optimized for low-latency authentication across global regions",
    "business.performance.architecture.item2": "Custom uptime SLA ensuring uninterrupted access",
    "business.performance.architecture.item3": "Elastic scaling to handle traffic surges without degradation",
    
    "business.performance.compliance.title": "Compliance & Data Management",
    "business.performance.compliance.item1": "Built-in compliance tools for GDPR, LGPD, HIPAA requirements",
    "business.performance.compliance.item2": "Comprehensive audit trails and reporting capabilities",
    "business.performance.compliance.item3": "Granular data residency controls and privacy management",
    
    "business.useCases.title": "Use Cases",
    "business.useCases.description": "Real-world applications that deliver tangible benefits.",
    
    "business.useCases.saas.title": "SaaS Platforms",
    "business.useCases.saas.description": "Enable seamless authentication for multi-tenant SaaS applications with granular role-based access control and user management.",
    
    "business.useCases.enterprise.title": "Enterprise Identity Management",
    "business.useCases.enterprise.description": "Manage workforce access, privileged accounts, and compliance auditing in large organizations with complex access needs.",
    
    "business.useCases.financial.title": "Financial Services & Fintech",
    "business.useCases.financial.description": "Enhance security with fraud detection, risk-based authentication, and AI-driven access control for sensitive financial operations.",
    
    "business.useCases.healthcare.title": "Healthcare & Regulated Industries",
    "business.useCases.healthcare.description": "Protect sensitive data with adaptive security and compliance-ready IAM tools that meet strict regulatory requirements.",
    
    "business.useCases.b2b.title": "B2B Marketplaces & Ecosystems",
    "business.useCases.b2b.description": "Secure authentication for vendors, partners, and clients in a multi-stakeholder environment with granular permissions.",
    
    "business.useCases.crossOrg.title": "Cross-Organization Collaboration",
    "business.useCases.crossOrg.description": "Enable secure access across organizational boundaries for partnerships, mergers, acquisitions, and joint ventures.",
    
    "business.cta.title": "Ready to transform your business identity management?",
    "business.cta.description": "Stay connected with confidence and focus on what truly matters—growing your business.",
    "business.viewPricing": "View Pricing",
    
    // CIAM Page
    "ciam.title": "CIAM",
    "ciam.hero.title": "Elevate Customer Identity Management",
    "ciam.hero.description": "Deliver exceptional user experiences with secure, seamless customer identity management.",
    
    "ciam.intro.title": "Customer Identity and Access Management",
    "ciam.intro.description": "Our CIAM solution empowers businesses to create secure, frictionless customer experiences at scale. Build customer trust with personalized journeys and robust security across all digital touchpoints.",
    "ciam.intro.imageAlt": "Customer identity management illustration",
    "ciam.getStarted": "Get Started",
    
    "ciam.features.title": "Key Features",
    "ciam.features.description": "Comprehensive identity management solutions designed for customer-facing applications.",
    
    "ciam.features.onboarding.title": "Streamlined Onboarding",
    "ciam.features.onboarding.description": "Frictionless registration with progressive profiling, social login options, and customizable user journeys that increase conversion rates.",
    
    "ciam.features.authentication.title": "Passwordless Authentication",
    "ciam.features.authentication.description": "Eliminate passwords with biometrics, passkeys, magic links, and mobile authenticators for improved security and user experience.",
    
    "ciam.features.consent.title": "Consent Management",
    "ciam.features.consent.description": "Comprehensive tools for managing user consent and preferences in compliance with global privacy regulations.",
    
    "ciam.features.personalization.title": "Customer Personalization",
    "ciam.features.personalization.description": "Unified customer profiles with preference management to deliver tailored experiences across all digital touchpoints.",
    
    "ciam.features.security.title": "Adaptive Security",
    "ciam.features.security.description": "Context-aware authentication that adjusts security requirements based on risk factors without compromising user experience.",
    
    "ciam.features.analytics.title": "User Analytics",
    "ciam.features.analytics.description": "Comprehensive insights into user behavior, authentication patterns, and conversion metrics to optimize customer journeys.",
    
    "ciam.scalability.title": "Enterprise-Grade Scalability",
    "ciam.scalability.description": "Built to handle millions of customer identities with high performance and reliability.",
    
    "ciam.scalability.architecture.title": "Scalable Architecture",
    "ciam.scalability.architecture.item1": "High-throughput authentication for peak traffic periods",
    "ciam.scalability.architecture.item2": "Global data centers ensuring low-latency access worldwide",
    "ciam.scalability.architecture.item3": "Elastic infrastructure that scales automatically with demand",
    
    "ciam.scalability.resilience.title": "System Resilience",
    "ciam.scalability.resilience.item1": "99.99% uptime SLA for mission-critical applications",
    "ciam.scalability.resilience.item2": "Robust disaster recovery and business continuity",
    "ciam.scalability.resilience.item3": "Distributed architecture preventing single points of failure",
    
    "ciam.compliance.title": "Compliance & Privacy",
    "ciam.compliance.description": "Comprehensive tools for managing regulatory compliance across global markets.",
    
    "ciam.compliance.regulations.title": "Global Compliance",
    "ciam.compliance.regulations.item1": "GDPR-ready data management and subject rights tools",
    "ciam.compliance.regulations.item2": "CCPA/CPRA compliance features for California consumers",
    "ciam.compliance.regulations.item3": "LGPD framework for Brazilian data protection",
    
    "ciam.compliance.privacy.title": "Privacy Controls",
    "ciam.compliance.privacy.item1": "Granular data residency and sovereignty options",
    "ciam.compliance.privacy.item2": "Privacy by design architecture and principles",
    "ciam.compliance.privacy.item3": "Comprehensive audit logs for compliance verification",
    
    "ciam.useCases.title": "Use Cases",
    "ciam.useCases.description": "Real-world applications that deliver tangible benefits.",
    
    "ciam.useCases.retail.title": "Retail & E-commerce",
    "ciam.useCases.retail.description": "Create seamless omnichannel experiences with unified customer profiles and secure, frictionless authentication across web and mobile touchpoints.",
    
    "ciam.useCases.financial.title": "Financial Services",
    "ciam.useCases.financial.description": "Meet strict regulatory requirements while delivering secure, convenient access to financial services with risk-based authentication and fraud prevention.",
    
    "ciam.useCases.media.title": "Media & Entertainment",
    "ciam.useCases.media.description": "Streamline subscription management and content access while gathering valuable insights on user preferences and consumption patterns.",
    
    "ciam.useCases.healthcare.title": "Healthcare",
    "ciam.useCases.healthcare.description": "Secure patient portals and telehealth platforms with HIPAA-compliant authentication and consent management for sensitive health data.",
    
    "ciam.useCases.travel.title": "Travel & Hospitality",
    "ciam.useCases.travel.description": "Deliver personalized travel experiences with seamless authentication across booking platforms, loyalty programs, and on-site services.",
    
    "ciam.benefits.title": "Business Benefits",
    "ciam.benefits.description": "Transform customer relationships and drive business growth with our CIAM solution.",
    
    "ciam.benefits.conversion.title": "Increased Conversion Rates",
    "ciam.benefits.conversion.description": "Frictionless registration and login processes that reduce abandonment and increase completion rates by up to 40%.",
    
    "ciam.benefits.loyalty.title": "Enhanced Customer Loyalty",
    "ciam.benefits.loyalty.description": "Personalized experiences that build lasting relationships and increase customer lifetime value through improved engagement.",
    
    "ciam.benefits.security.title": "Reduced Fraud Costs",
    "ciam.benefits.security.description": "Advanced security measures that prevent account takeovers and unauthorized access, protecting your business and customers.",
    
    "ciam.benefits.compliance.title": "Simplified Compliance",
    "ciam.benefits.compliance.description": "Automated tools for maintaining regulatory compliance across multiple jurisdictions, reducing legal risks and penalties.",
    
    "ciam.cta.title": "Ready to transform your customer experience?",
    "ciam.cta.description": "Discover how our CIAM solution can help you build secure, seamless customer relationships that drive business growth.",
    "ciam.viewPricing": "View Pricing"
  },
  "pt-BR": {
    // Navigation
    "nav.home": "Início",
    "nav.products": "Produtos",
    "nav.pricing": "Preços",
    "nav.contactUs": "Fale Conosco",
    
    // Products
    "products.discoverMore": "Descubra Mais",
    
    // Theme & Language
    "theme.light": "Modo Claro",
    "theme.dark": "Modo Escuro",
    "lang.en": "Inglês",
    "lang.pt": "Português",
    
    // Hero Section
    "hero.tagline": "Identidades Seguras Além de Senhas",
    "hero.title": "Adote uma Experiência Digital Sem Senhas",
    "hero.description": "Transforme a forma como você interage online com nossas soluções de segurança sem senha, projetadas para acesso sem esforço sem comprometer a segurança.",
    "hero.explorePricing": "Explorar Preços",
    "hero.exploreProducts": "Explorar Produtos",
    
    // Features Section
    "features.title": "Recursos Completos da Plataforma",
    "features.description": "Tudo o que você precisa para gerenciar eficientemente organizações, estruturas de cobrança e níveis de acesso.",
    "features.multiOrg": "Gerenciamento Multi-Organização",
    "features.multiOrgDesc": "Crie e gerencie várias organizações sob uma única conta de cliente para operações simplificadas.",
    "features.billing": "Estrutura de Cobrança Flexível",
    "features.billingDesc": "Escolha entre modelos de cobrança baseados em assinatura ou uso, adequados às suas necessidades.",
    "features.access": "Controles de Acesso por Níveis",
    "features.accessDesc": "Defina diferentes níveis de acesso, do Básico ao Empresarial, com configurações granulares de permissão.",
    "features.roles": "Gerenciamento de Função de Usuário",
    "features.rolesDesc": "Atribua usuários a organizações específicas com funções e permissões personalizáveis.",
    "features.identityDirectory": "Diretório Universal de Identidade",
    "features.identityDirectoryDesc": "Chega de dados de usuários dispersos. Armazene perfis, gerencie credenciais e aplique políticas de acesso facilmente em um local seguro.",
    "features.analytics": "Análises de Uso",
    "features.analyticsDesc": "Métricas e relatórios detalhados sobre padrões de consumo em todas as suas organizações.",
    "features.integration": "Capacidades de Integração",
    "features.integrationDesc": "Conecte-se com sistemas externos através de endpoints de API abrangentes.",
    "features.multiTenant": "Arquitetura Multi-Tenant",
    "features.multiTenantDesc": "Escalone suas operações com eficiência com nossa infraestrutura multi-tenant otimizada.",
    
    // Products Page
    "products.solutions": "Nossas Soluções",
    "products.title": "Soluções de Gerenciamento de Identidade",
    "products.description": "Nossas soluções de gerenciamento de identidade oferecem autenticação segura, tranquila e escalável. Projetadas para aumentar a segurança e melhorar a experiência do usuário, nossos produtos garantem conformidade com os padrões modernos de autenticação.",
    "products.workforce": "Keystle Workforce",
    "products.workforceDesc": "A pedra angular da autenticação sem senha, oferecendo uma forma segura e contínua de gerenciar o acesso do usuário final a aplicativos, APIs e serviços. Projetado para escalabilidade e versatilidade para aumentar a segurança enquanto oferece experiências superiores ao usuário.",
    "products.b2b": "Keystle B2B",
    "products.b2bDesc": "Uma solução robusta de gerenciamento de identidade projetada para ajudar empresas a lidar com milhões de identidades de clientes de forma segura e eficiente. Capacita organizações a fornecer experiências personalizadas ao usuário, mantendo a conformidade com rigorosos regulamentos de privacidade de dados.",
    "products.ciam": "Keystle CIAM",
    "products.ciamDesc": "Projetado para fornecer às empresas uma maneira sem atrito, segura e escalável de gerenciar identidades de clientes em várias plataformas. Permite autenticação sem senha, login social, segurança adaptativa e telemetria em tempo real.",
    "products.keyFeatures": "Principais Características",
    
    // Security Section
    "security.trustSecurity": "Confiança & Segurança",
    "security.title": "Segurança & Conformidade",
    "security.description": "Na Keystle, segurança e conformidade são inegociáveis. Nossa abordagem abrangente para proteger seus dados e garantir a conformidade regulatória proporciona tranquilidade para sua organização e seus clientes.",
    "security.architecture": "Arquitetura de Segurança Robusta",
    "security.architectureDesc": "Nossa plataforma é construída com abordagem de segurança em primeiro lugar, implementando criptografia líder do setor, comunicações seguras e controles de acesso rigorosos.",
    "security.encryption": "Padrões de Criptografia",
    "security.encryptionDesc": "Todos os dados sensíveis são criptografados em repouso e em trânsito usando AES-256 e TLS 1.3, garantindo que suas informações permaneçam protegidas em todos os pontos de contato.",
    "security.compliance": "Requisitos de Conformidade",
    "security.complianceDesc": "A Keystle mantém requisitos de conformidade com regulamentações globais, incluindo GDPR, CCPA, ISO 27001, SOC 2 e outras estruturas específicas do setor.",
    "security.dataProtection": "Proteção de Dados",
    "security.dataProtectionDesc": "Implementamos segregação estrita de dados, auditorias de segurança regulares e monitoramento abrangente para proteger suas informações sensíveis.",
    
    // Pricing Page
    "pricing.title": "Estrutura de Preços Flexível",
    "pricing.description": "Escolha entre modelos de preços baseados em assinatura ou uso. Pague mensalmente ou anualmente com opções para centralizar ou individualizar a cobrança em suas organizações.",
    "pricing.plans": "Planos de Preços Flexíveis",
    "pricing.plansDesc": "Escolha o plano perfeito para as necessidades da sua organização com nossa estrutura de preços transparente.",
    "pricing.monthly": "Mensal",
    "pricing.annually": "Anual",
    "pricing.save": "Economize",
    "pricing.withAnnual": "com faturamento anual",
    "pricing.min1500": "Valor mínimo mensal de $1500 é aplicável",
    "pricing.min5000": "Mínimo de 5000 usuários mensais",
    "pricing.perMonth": "/mês",
    "pricing.perYear": "/ano",
    "pricing.startWith": "Começar com",
    "pricing.contactSales": "Contatar Vendas",
    
    // Tiers Page
    "tiers.title": "Estrutura de Acesso por Níveis",
    "tiers.description": "Defina níveis de acesso para diferentes tipos de clientes e organizações. Cada nível fornece um conjunto diferente de recursos e capacidades para atender às suas necessidades específicas.",
    "tiers.viewPricing": "Ver Preços",
    "tiers.comparison": "Comparação de Níveis de Acesso",
    "tiers.comparisonDesc": "Compare nossos níveis de acesso para encontrar o ajuste perfeito para as necessidades da sua organização",
    "tiers.basic": "Básico",
    "tiers.basicDesc": "Recursos essenciais para pequenas equipes",
    "tiers.standard": "Padrão",
    "tiers.standardDesc": "Recursos avançados para equipes em crescimento",
    "tiers.enterprise": "Empresarial",
    "tiers.enterpriseDesc": "Solução completa para grandes organizações",
    "tiers.select": "Selecionar",
    "tiers.tier": "Nível",
    
    // Error and Coming Soon Pages
    "error.404": "Erro 404",
    "error.title": "Página Não Encontrada",
    "error.description": "Desculpe, não conseguimos encontrar a página que você está procurando. Ela pode ter sido movida ou não existe.",
    "error.returnHome": "Retornar ao Início",
    "comingSoon": "Em Breve",
    "comingSoon.title": "Desculpe pelo Inconveniente",
    "comingSoon.description": "Estamos construindo um produto incrível e esta página estará disponível em breve. Fique atento para atualizações empolgantes enquanto continuamos a desenvolver recursos que transformarão sua experiência de identidade digital.",
    
    // Footer
    "footer.product": "Produto",
    "footer.resources": "Recursos",
    "footer.company": "Empresa",
    "footer.legal": "Legal",
    "footer.features": "Recursos",
    "footer.docs": "Documentação",
    "footer.api": "Referência da API",
    "footer.integrations": "Integrações",
    "footer.support": "Suporte",
    "footer.about": "Sobre",
    "footer.blog": "Blog",
    "footer.careers": "Carreiras",
    "footer.contact": "Contato",
    "footer.privacy": "Privacidade",
    "footer.terms": "Termos",
    "footer.security": "Segurança",
    "footer.copyright": "Todos os direitos reservados.",
    "footer.description": "Capacitando organizações com soluções flexíveis de faturamento e gerenciamento de acesso.",
    
    // About Page
    "about.aboutKeystle": "Sobre a Keystle",
    "about.heroTitle": "Capacitando um Amanhã Sem Senhas",
    "about.heroSubtitle": "Identidade Segura Além da Senha",
    "about.introTitle": "Bem-vindo à Keystle",
    "about.introPara1": "Na era digital, a identidade é mais do que apenas um login—é a chave para experiências seguras, tranquilas e sem atritos. No entanto, os métodos tradicionais de autenticação são falhos: as senhas são inconvenientes, vulneráveis e comprometem tanto a segurança quanto a experiência do usuário.",
    "about.introPara2": "Estamos motivados a transformar essa realidade e redefinir a autenticação. Eliminamos senhas e tornamos o acesso mais seguro, intuitivo e inteligente. Nossa tecnologia combina inteligência artificial, telemetria avançada e autenticação sem senha, garantindo máxima proteção sem comprometer a usabilidade.",
    "about.introHighlight": "Mais segurança. Menos atrito. Identidade ilimitada.",
    "about.feature1Title": "Segurança Sem Senha",
    "about.feature1Desc": "Experimente segurança aprimorada com nossas soluções sem senha, eliminando barreiras de autenticação enquanto protege identidades de usuários.",
    "about.feature2Title": "Integração Perfeita",
    "about.feature2Desc": "Nossas soluções são projetadas para fácil integração em várias plataformas, simplificando experiências de usuário e maximizando a eficiência operacional.",
    "about.feature3Title": "Tecnologia Impulsionada por IA",
    "about.feature3Desc": "Aproveite ferramentas avançadas impulsionadas por IA que se adaptam continuamente, garantindo segurança robusta e acesso amigável a recursos digitais.",
    "about.historyTitle": "NOSSA HISTÓRIA",
    "about.historyHeading": "O Que Pensamos sobre o Mundo Atual de Identidade",
    "about.historySubheading": "Por Que Fundamos a Keystle?",
    "about.historyPara1": "Nossa visão do cenário moderno de identidade nos levou a buscar algo melhor. Reconhecemos uma falha fundamental na forma como as identidades digitais são gerenciadas, a segurança é cara e ineficaz, enquanto os usuários suportam processos de autenticação ultrapassados e frustrantes, seja como funcionários ou clientes.",
    "about.historyPara2": "Qual é o problema? Um sistema quebrado que trata a identidade como um obstáculo em vez de um facilitador.",
    "about.historyHighlight": "Nos recusamos a aceitar isso.",
    "about.visionHeading": "E se a identidade pudesse ser segura sem ser um incômodo?",
    "about.visionPara1": "É por isso que construímos uma plataforma IDaaS (Identity-as-a-Service) projetada com a experiência do usuário em seu núcleo, garantindo acesso seguro e contínuo para funcionários e clientes.",
    "about.visionPara2": "Nossas soluções sem senha não apenas melhoram a segurança, elas a redefinem. Usando autenticação impulsionada por IA e telemetria avançada, fornecemos proteção sem atrito onde a identidade é invisível quando tudo está certo e impenetrável quando algo está errado.",
    "about.visionPara3": "Desafiamos o modelo ultrapassado porque acreditamos que a identidade deve trabalhar para as pessoas, não contra elas. Um mundo sem senhas não é apenas possível, é necessário.",
    "about.visionPara4": "Junte-se a nós na construção de um futuro onde o acesso é sem esforço, a segurança é perfeita e a confiança é incorporada.",
    "about.quote": "Vida sem senhas: Seu portal para segurança sem problemas.",
    "about.quoteContext": "Criamos uma maneira revolucionária para empresas abordarem a segurança. Nossas inovadoras soluções IAM ajudaram numerosas organizações a aumentar a segurança e a satisfação do usuário, reduzindo o atrito de login e aumentando a eficiência operacional. Junte-se a nós em uma jornada rumo a um futuro sem senhas.",
    "about.quoteAttribution": "— Danilo, Deli e Gilmar, Fundadores da Keystle",
    "about.purposeTitle": "NOSSO PROPÓSITO",
    "about.purposeSubtitle": "Definindo Nossa Missão",
    "about.purposeText": "Capacitar empresas e indivíduos fornecendo soluções de Gerenciamento de Identidade e Acesso (IAM) sem senha de ponta que eliminam a necessidade de senhas, garantindo segurança, facilidade de uso e escalabilidade. Com inovação implacável em IA e telemetria, nos esforçamos para proteger identidades digitais, simplificar o acesso e redefinir experiências de usuário, permitindo um futuro seguro e sem atrito em um cenário cada vez mais digital.",
    "about.principlesTitle": "PRINCÍPIOS FUNDAMENTAIS",
    "about.principlesSubtitle": "Nossos Valores Orientadores",
    "about.principlesText": "Segurança, inovação e experiências perfeitas definem a Keystle. Estamos comprometidos com confiança, acessibilidade e tecnologia de ponta, garantindo que o gerenciamento de identidade seja seguro e sem esforço. Ao priorizar Identidade como Serviço, design centrado no usuário e aprendizado contínuo, capacitamos empresas com soluções sem senha, escaláveis e compatíveis que redefinem como as pessoas interagem com a identidade digital.",
    "about.ctaTitle": "Pronto para Experimentar a Segurança Sem Senha?",
    
    // Workforce Page
    "workforce.title": "WORKFORCE",
    "workforce.hero.title": "Sem Senhas, Apenas Segurança",
    "workforce.hero.description": "Pare de redefinir senhas, comece a proteger o acesso. Sem senhas. Sem atrito. Sem violações.",
    
    "workforce.intro.title": "Aumente a Segurança, Entregue Experiências Incríveis",
    "workforce.intro.description": "Entregamos segurança incomparável e experiências de usuário perfeitas em escala. Projetado para versatilidade, capacita organizações com autenticação moderna, multilocação e controle de acesso dinâmico, adaptando-se sem esforço às necessidades corporativas.",
    "workforce.intro.imageAlt": "Ilustração de gerenciamento de identidade empresarial",
    "workforce.getStarted": "Comece Agora",
    
    "workforce.features.title": "Recursos Principais",
    "workforce.features.description": "Soluções abrangentes de autenticação e gerenciamento de acesso projetadas para requisitos empresariais.",
    
    "workforce.features.authentication.title": "Autenticação",
    "workforce.features.authentication.item1": "OTP e Email MFA",
    "workforce.features.authentication.item2": "Suporte OAuth 2.0, OIDC, SAML",
    "workforce.features.authentication.item3": "Design Sem Senha Primeiro",
    "workforce.features.authentication.item4": "Integração com IAM em nuvem (Google, AWS)",
    
    "workforce.features.sso.title": "Login Único (SSO)",
    "workforce.features.sso.item1": "Login centralizado para múltiplos aplicativos",
    "workforce.features.sso.item2": "Redução da fadiga de login",
    "workforce.features.sso.item3": "Integração empresarial simplificada",
    "workforce.features.sso.item4": "Compatibilidade com serviços em nuvem",
    
    "workforce.features.scim.title": "Identidade entre Domínios (SCIM)",
    "workforce.features.scim.item1": "Provisionamento automatizado de usuários",
    "workforce.features.scim.item2": "Redução de sobrecarga administrativa",
    "workforce.features.scim.item3": "Sincronização de atributos de identidade",
    "workforce.features.scim.item4": "Interoperabilidade entre provedores",
    
    "workforce.features.authorization.title": "Autorização Dinâmica",
    "workforce.features.authorization.item1": "Controle de Acesso Baseado em Função (RBAC)",
    "workforce.features.authorization.item2": "Controle de Acesso Baseado em Atributos (ABAC)",
    "workforce.features.authorization.item3": "Controle de Acesso Baseado em Políticas (PBAC)",
    "workforce.features.authorization.item4": "Segurança adaptativa e sensível ao contexto",
    
    "workforce.addons.title": "Complementos",
    "workforce.addons.description": "Recursos de segurança aprimorados para proteger sua organização contra ameaças emergentes.",
    
    "workforce.addons.ai.title": "Segurança Impulsionada por IA",
    "workforce.addons.ai.description": "Detecção e prevenção em tempo real alimentadas por IA para identificar e bloquear atividades suspeitas, com detecção abrangente de violações para todas as identidades.",
    
    "workforce.addons.mfa.title": "MFA Enterprise",
    "workforce.addons.mfa.description": "Autenticação Multi-Fator com fatores Whatsapp, Chamada, SMS para uma camada adicional de segurança, garantindo conformidade com padrões regulatórios.",
    
    "workforce.addons.threat.title": "Mitigação de Ameaças",
    "workforce.addons.threat.description": "A autenticação baseada em risco ajusta os requisitos de segurança com base em fatores contextuais, enquanto a detecção de fraude impulsionada por IA e análise comportamental minimizam potenciais ameaças.",
    
    "workforce.useCases.title": "Casos de Uso",
    "workforce.useCases.description": "Aplicações do mundo real do KEYSTLE Workforce que entregam benefícios tangíveis.",
    
    "workforce.useCases.global.title": "Autenticação Global de Força de Trabalho",
    "workforce.useCases.global.item1": "Acesso seguro e sem senha para força de trabalho distribuída",
    "workforce.useCases.global.item2": "Políticas granulares de autorização condicional",
    "workforce.useCases.global.item3": "Segurança sensível ao contexto para funcionários remotos",
    "workforce.useCases.global.item4": "Proteção para ambientes de trabalho híbridos",
    
    "workforce.useCases.corporate.title": "Implantação Corporativa de SSO",
    "workforce.useCases.corporate.item1": "Login simplificado em aplicações empresariais",
    "workforce.useCases.corporate.item2": "Altos padrões de segurança e conformidade",
    "workforce.useCases.corporate.item3": "Redução de sobrecarga de TI para redefinições de senha",
    "workforce.useCases.corporate.item4": "Processos simplificados de recuperação de conta",
    
    "workforce.capabilities.title": "Capacidades",
    "workforce.capabilities.description": "Recursos de desempenho e conformidade de nível empresarial construídos para organizações modernas.",
    
    "workforce.capabilities.performance.title": "Desempenho e Escalabilidade",
    "workforce.capabilities.performance.latency.title": "Login de Baixa Latência",
    "workforce.capabilities.performance.latency.description": "Processe milhões de solicitações de autenticação simultâneas sem degradação de desempenho.",
    "workforce.capabilities.performance.availability.title": "Alta Disponibilidade",
    "workforce.capabilities.performance.availability.description": "Serviço ininterrupto com mecanismos robustos de failover e balanceamento de carga.",
    "workforce.capabilities.performance.residency.title": "Residência de Dados",
    "workforce.capabilities.performance.residency.description": "Seus dados na região que você escolher.",
    "workforce.capabilities.performance.database.title": "Banco de Dados Universal",
    "workforce.capabilities.performance.database.description": "Fonte única habilitada por diretório em nuvem e integrações empresariais.",
    
    "workforce.capabilities.compliance.title": "Conformidade",
    "workforce.capabilities.compliance.gdpr.title": "Conformidade GDPR",
    "workforce.capabilities.compliance.gdpr.description": "Medidas de proteção de dados para regulamentos da UE, incluindo gerenciamento de consentimento do usuário.",
    "workforce.capabilities.compliance.hipaa.title": "Padrões HIPAA",
    "workforce.capabilities.compliance.hipaa.description": "Manuseio seguro de informações de saúde protegidas com controles de acesso rigorosos.",
    "workforce.capabilities.compliance.lgpd.title": "Estrutura LGPD",
    "workforce.capabilities.compliance.lgpd.description": "Conformidade com a lei brasileira de proteção de dados para direitos do titular dos dados.",
    "workforce.capabilities.compliance.custom.title": "Conformidade Personalizável",
    "workforce.capabilities.compliance.custom.description": "Estrutura adaptável para regulamentos específicos do setor com recursos de auditoria.",
    
    "workforce.cta.title": "Pronto para proteger sua força de trabalho?",
    "workforce.cta.description": "Descubra como o Workforce pode transformar a postura de segurança da sua organização enquanto melhora a experiência do usuário.",
    "workforce.viewPricing": "Ver Preços",
    
    // Business Page
    "business.title": "NEGÓCIOS",
    "business.hero.title": "Capacitando Negócios através do Gerenciamento de Identidade",
    "business.hero.description": "Gerencie milhões de identidades com segurança e eficiência incomparáveis.",
    
    "business.intro.title": "Gerenciamento Avançado de Identidade B2B",
    "business.intro.description": "O Gerenciador de Identidade para Negócios é a solução definitiva para empresas que buscam controle de acesso inteligente, personalização avançada e conformidade rigorosa de privacidade. Com escalabilidade perfeita e integrações de ponta, proporciona uma experiência sem atritos para os usuários enquanto garante proteção robusta de dados.",
    "business.intro.imageAlt": "Ilustração de gerenciamento de identidade empresarial",
    "business.getStarted": "Começar Agora",
    
    "business.features.title": "Recursos Principais",
    "business.features.description": "Soluções abrangentes de gerenciamento de identidade projetadas para requisitos empresariais modernos.",
    
    "business.features.dashboard.title": "Painel Unificado",
    "business.features.dashboard.description": "Um único painel para atividades de usuário, acompanhamento de conformidade e aplicação de políticas para aplicativos distintos e múltiplos locatários.",
    
    "business.features.multiTenant.title": "Gerenciamento de Identidade Multi-Tenant",
    "business.features.multiTenant.description": "Autenticação segura e escalável para múltiplos clientes, parceiros e equipes internas com ambientes isolados.",
    
    "business.features.passwordless.title": "Autenticação Sem Senha",
    "business.features.passwordless.description": "Elimine senhas com biometria, chaves de acesso e links mágicos para uma experiência de usuário sem atritos e segurança aprimorada.",
    
    "business.features.sso.title": "Login Único (SSO)",
    "business.features.sso.description": "Acesso contínuo entre múltiplos aplicativos com uma única autenticação, reduzindo atritos e melhorando a segurança.",
    
    "business.features.mfa.title": "MFA com OTP e Email",
    "business.features.mfa.description": "Experiências de login seguras usando senhas de uso único via autenticador de aplicativo e verificação de email, fornecendo uma camada adicional de segurança além das senhas.",
    
    "business.features.api.title": "Abordagem API-First",
    "business.features.api.description": "Integração perfeita com OAuth, SAML, OpenID Connect e SCIM para implantação rápida e interoperabilidade abrangente.",
    
    "business.addons.title": "Complementos de Segurança e Inteligência",
    "business.addons.description": "Capacidades de segurança aprimoradas para proteger sua organização e clientes.",
    
    "business.addons.analytics.title": "Análise Comportamental",
    "business.addons.analytics.description": "Ferramentas incorporadas analisam o comportamento do usuário e padrões de acesso para detectar anomalias, prevenir ameaças e melhorar o engajamento.",
    
    "business.addons.fraud.title": "Prevenção de Fraude",
    "business.addons.fraud.description": "Identificação de dispositivo e pontuação de risco com IA para prevenir aquisições de contas e tentativas de acesso não autorizado.",
    
    "business.addons.adaptive.title": "Autenticação Adaptativa",
    "business.addons.adaptive.description": "Autenticação sensível ao contexto ajusta níveis de segurança dinamicamente com base na atividade do usuário, localização e fatores de risco.",
    
    "business.performance.title": "Desempenho de Nível Empresarial",
    "business.performance.description": "Construído para lidar com necessidades de autenticação de alto volume sem comprometer velocidade ou confiabilidade.",
    
    "business.performance.architecture.title": "Arquitetura de Alto Desempenho",
    "business.performance.architecture.item1": "Otimizado para autenticação de baixa latência em regiões globais",
    "business.performance.architecture.item2": "SLA de tempo de atividade personalizado garantindo acesso ininterrupto",
    "business.performance.architecture.item3": "Escalabilidade elástica para lidar com picos de tráfego sem degradação",
    
    "business.performance.compliance.title": "Conformidade e Gerenciamento de Dados",
    "business.performance.compliance.item1": "Ferramentas de conformidade incorporadas para requisitos GDPR, LGPD, HIPAA",
    "business.performance.compliance.item2": "Trilhas de auditoria abrangentes e capacidades de relatórios",
    "business.performance.compliance.item3": "Controles granulares de residência de dados e gerenciamento de privacidade",
    
    "business.useCases.title": "Casos de Uso",
    "business.useCases.description": "Aplicações do mundo real que proporcionam benefícios tangíveis.",
    
    "business.useCases.saas.title": "Plataformas SaaS",
    "business.useCases.saas.description": "Habilite autenticação perfeita para aplicativos SaaS multi-tenant com controle de acesso baseado em função granular e gerenciamento de usuários.",
    
    "business.useCases.enterprise.title": "Gerenciamento de Identidade Empresarial",
    "business.useCases.enterprise.description": "Gerencie acesso da força de trabalho, contas privilegiadas e auditoria de conformidade em grandes organizações com necessidades complexas de acesso.",
    
    "business.useCases.financial.title": "Serviços Financeiros e Fintech",
    "business.useCases.financial.description": "Aumente a segurança com detecção de fraude, autenticação baseada em risco e controle de acesso baseado em IA para operações financeiras sensíveis.",
    
    "business.useCases.healthcare.title": "Saúde e Indústrias Regulamentadas",
    "business.useCases.healthcare.description": "Proteja dados sensíveis com segurança adaptativa e ferramentas de IAM prontas para conformidade que atendem a requisitos regulatórios rigorosos.",
    
    "business.useCases.b2b.title": "Marketplaces B2B e Ecossistemas",
    "business.useCases.b2b.description": "Autenticação segura para fornecedores, parceiros e clientes em um ambiente multiparticipante com permissões granulares.",
    
    "business.useCases.crossOrg.title": "Colaboração Entre Organizações",
    "business.useCases.crossOrg.description": "Habilite acesso seguro através de fronteiras organizacionais para parcerias, fusões, aquisições e joint ventures.",
    
    "business.cta.title": "Pronto para transformar o gerenciamento de identidade do seu negócio?",
    "business.cta.description": "Mantenha-se conectado com confiança e foque no que realmente importa—fazer seu negócio crescer.",
    "business.viewPricing": "Ver Preços",
    
    // CIAM Page
    "ciam.title": "CIAM",
    "ciam.hero.title": "Eleve o Gerenciamento de Identidade do Cliente",
    "ciam.hero.description": "Ofereça experiências excepcionais ao usuário com gerenciamento seguro e sem problemas da identidade do cliente.",
    
    "ciam.intro.title": "Gerenciamento de Identidade e Acesso do Cliente",
    "ciam.intro.description": "Nossa solução CIAM capacita empresas a criar experiências de cliente seguras e sem atritos em escala. Construa a confiança do cliente com jornadas personalizadas e segurança robusta em todos os pontos de contato digitais.",
    "ciam.intro.imageAlt": "Ilustração de gerenciamento de identidade do cliente",
    "ciam.getStarted": "Começar Agora",
    
    "ciam.features.title": "Recursos Principais",
    "ciam.features.description": "Soluções abrangentes de gerenciamento de identidade projetadas para aplicações voltadas ao cliente.",
    
    "ciam.features.onboarding.title": "Integração Simplificada",
    "ciam.features.onboarding.description": "Registro sem atritos com perfil progressivo, opções de login social e jornadas de usuário personalizáveis que aumentam as taxas de conversão.",
    
    "ciam.features.authentication.title": "Autenticação Sem Senha",
    "ciam.features.authentication.description": "Elimine senhas com biometria, chaves de acesso, links mágicos e autenticadores móveis para melhorar a segurança e a experiência do usuário.",
    
    "ciam.features.consent.title": "Gerenciamento de Consentimento",
    "ciam.features.consent.description": "Ferramentas abrangentes para gerenciar o consentimento e as preferências do usuário em conformidade com regulamentos globais de privacidade.",
    
    "ciam.features.personalization.title": "Personalização do Cliente",
    "ciam.features.personalization.description": "Perfis de cliente unificados com gerenciamento de preferências para oferecer experiências personalizadas em todos os pontos de contato digitais.",
    
    "ciam.features.security.title": "Segurança Adaptativa",
    "ciam.features.security.description": "Autenticação sensível ao contexto que ajusta os requisitos de segurança com base em fatores de risco sem comprometer a experiência do usuário.",
    
    "ciam.features.analytics.title": "Análise de Usuário",
    "ciam.features.analytics.description": "Insights abrangentes sobre comportamento do usuário, padrões de autenticação e métricas de conversão para otimizar jornadas do cliente.",
    
    "ciam.scalability.title": "Escalabilidade de Nível Empresarial",
    "ciam.scalability.description": "Construído para lidar com milhões de identidades de clientes com alto desempenho e confiabilidade.",
    
    "ciam.scalability.architecture.title": "Arquitetura Escalável",
    "ciam.scalability.architecture.item1": "Autenticação de alto rendimento para períodos de pico de tráfego",
    "ciam.scalability.architecture.item2": "Data centers globais garantindo acesso de baixa latência em todo o mundo",
    "ciam.scalability.architecture.item3": "Infraestrutura elástica que escala automaticamente com a demanda",
    
    "ciam.scalability.resilience.title": "Resiliência do Sistema",
    "ciam.scalability.resilience.item1": "SLA de tempo de atividade de 99,99% para aplicações críticas",
    "ciam.scalability.resilience.item2": "Recuperação de desastres robusta e continuidade de negócios",
    "ciam.scalability.resilience.item3": "Arquitetura distribuída prevenindo pontos únicos de falha",
    
    "ciam.compliance.title": "Conformidade e Privacidade",
    "ciam.compliance.description": "Ferramentas abrangentes para gerenciar a conformidade regulatória em mercados globais.",
    
    "ciam.compliance.regulations.title": "Conformidade Global",
    "ciam.compliance.regulations.item1": "Gerenciamento de dados pronto para GDPR e ferramentas de direitos do titular",
    "ciam.compliance.regulations.item2": "Recursos de conformidade CCPA/CPRA para consumidores da Califórnia",
    "ciam.compliance.regulations.item3": "Estrutura LGPD para proteção de dados brasileira",
    
    "ciam.compliance.privacy.title": "Controles de Privacidade",
    "ciam.compliance.privacy.item1": "Opções granulares de residência e soberania de dados",
    "ciam.compliance.privacy.item2": "Arquitetura e princípios de privacidade por design",
    "ciam.compliance.privacy.item3": "Logs de auditoria abrangentes para verificação de conformidade",
    
    "ciam.useCases.title": "Casos de Uso",
    "ciam.useCases.description": "Aplicações do mundo real que proporcionam benefícios tangíveis.",
    
    "ciam.useCases.retail.title": "Varejo e E-commerce",
    "ciam.useCases.retail.description": "Crie experiências omnicanal perfeitas com perfis de cliente unificados e autenticação segura e sem atrito em pontos de contato web e móveis.",
    
    "ciam.useCases.financial.title": "Serviços Financeiros",
    "ciam.useCases.financial.description": "Atenda a requisitos regulatórios rigorosos enquanto oferece acesso seguro e conveniente a serviços financeiros com autenticação baseada em risco e prevenção de fraude.",
    
    "ciam.useCases.media.title": "Mídia e Entretenimento",
    "ciam.useCases.media.description": "Simplifique o gerenciamento de assinaturas e acesso a conteúdo enquanto reúne insights valiosos sobre preferências e padrões de consumo do usuário.",
    
    "ciam.useCases.healthcare.title": "Saúde",
    "ciam.useCases.healthcare.description": "Portais de pacientes seguros e plataformas de telemedicina com autenticação compatível com HIPAA e gerenciamento de consentimento para dados de saúde sensíveis.",
    
    "ciam.useCases.travel.title": "Viagens e Hospitalidade",
    "ciam.useCases.travel.description": "Ofereça experiências de viagem personalizadas com autenticação perfeita em plataformas de reserva, programas de fidelidade e serviços no local.",
    
    "ciam.benefits.title": "Benefícios para o Negócio",
    "ciam.benefits.description": "Transforme relacionamentos com clientes e impulsione o crescimento do negócio com nossa solução CIAM.",
    
    "ciam.benefits.conversion.title": "Aumento nas Taxas de Conversão",
    "ciam.benefits.conversion.description": "Processos de registro e login sem atrito que reduzem o abandono e aumentam as taxas de conclusão em até 40%.",
    
    "ciam.benefits.loyalty.title": "Fidelidade do Cliente Aprimorada",
    "ciam.benefits.loyalty.description": "Experiências personalizadas que constroem relacionamentos duradouros e aumentam o valor vitalício do cliente através de engajamento aprimorado.",
    
    "ciam.benefits.security.title": "Redução de Custos de Fraude",
    "ciam.benefits.security.description": "Medidas de segurança avançadas que previnem tomadas de conta e acesso não autorizado, protegendo seu negócio e clientes.",
    
    "ciam.benefits.compliance.title": "Conformidade Simplificada",
    "ciam.benefits.compliance.description": "Ferramentas automatizadas para manter a conformidade regulatória em múltiplas jurisdições, reduzindo riscos legais e penalidades.",
    
    "ciam.cta.title": "Pronto para transformar sua experiência de cliente?",
    "ciam.cta.description": "Descubra como nossa solução CIAM pode ajudá-lo a construir relacionamentos com clientes seguros e sem problemas que impulsionam o crescimento do negócio.",
    "ciam.viewPricing": "Ver Preços"
  }
};

export function LanguageProvider({
  children,
  defaultLanguage = "en-US",
  storageKey = "keystle-language",
  ...props
}: LanguageProviderProps) {
  const [language, setLanguage] = useState<Language>(
    () => (localStorage.getItem(storageKey) as Language) || defaultLanguage
  );

  useEffect(() => {
    localStorage.setItem(storageKey, language);
    // Add html lang attribute for accessibility and SEO
    document.documentElement.setAttribute("lang", language.split("-")[0]);
  }, [language, storageKey]);

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageProviderContext.Provider
      value={{
        language,
        setLanguage,
        t,
      }}
      {...props}
    >
      {children}
    </LanguageProviderContext.Provider>
  );
}

export const useLanguage = () => {
  const context = useContext(LanguageProviderContext);
  
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  
  return context;
};

